import { useEffect, useState } from 'react';

import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { useDispatch } from 'react-redux';
import { Navigate, Route, Routes } from 'react-router-dom';

import {
    isBackofficeConnected,
    loginBackoffice,
    onBackofficeUnauthorized,
} from '@/api/backofficeAuth';
import { hubApi } from '@/api/hubApi';
import { AppLayout } from '@/layout/AppLayout';
import { AgentsPage } from '@/pages/AgentsPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { LoginPage } from '@/pages/LoginPage';
import { OrganizationsListPage } from '@/pages/OrganizationsListPage';
import { ProtocolsPage } from '@/pages/ProtocolsPage';
import type { AppDispatch } from '@/app/store';

export function App() {
    const dispatch = useDispatch<AppDispatch>();
    const [authorized, setAuthorized] = useState(isBackofficeConnected());
    const [autoAuthorizing, setAutoAuthorizing] = useState(!isBackofficeConnected());

    useEffect(() => {
        if (isBackofficeConnected()) return;
        let cancelled = false;
        (async () => {
            // An empty token is what the hub's own login route treats as a
            // no-op success when BACKOFFICE_TOKEN isn't configured
            // server-side (see backofficeAuth.routes.js) — so this silently
            // "logs in" for free in that mode, and otherwise just fails
            // closed, same as any other wrong token.
            const loginOk = await loginBackoffice('');
            if (cancelled || !loginOk) {
                if (!cancelled) setAutoAuthorizing(false);
                return;
            }
            // The login route being "open" doesn't mean the rest of the hub
            // is: requireBackofficeToken (gating every list below) fails
            // closed in that same no-token-configured case, so a dashboard
            // rendered on the login result alone would mount and 401 on all
            // four of its queries at once. Confirm against one of those
            // protected routes first — through hubApi itself, so a rejection
            // here also runs through its own 401 handling below — before
            // ever rendering the routed app.
            const probe = dispatch(hubApi.endpoints.listOrgTokens.initiate());
            try {
                await probe.unwrap();
                if (!cancelled) setAuthorized(true);
            } catch {
                // Left unauthorized — onBackofficeUnauthorized below already
                // ran off this same rejection.
            } finally {
                probe.unsubscribe();
                if (!cancelled) setAutoAuthorizing(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [dispatch]);

    useEffect(() => onBackofficeUnauthorized(() => setAuthorized(false)), []);

    if (autoAuthorizing) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '100vh',
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    if (!authorized) {
        return <LoginPage onAuthenticated={() => setAuthorized(true)} />;
    }

    return (
        <Routes>
            <Route element={<AppLayout onLogout={() => setAuthorized(false)} />}>
                <Route index element={<Navigate to="/dashboard" replace />} />
                <Route path="dashboard" element={<DashboardPage />} />
                <Route path="organizations" element={<OrganizationsListPage />} />
                <Route path="agents" element={<AgentsPage />} />
                <Route path="protocols" element={<ProtocolsPage />} />
            </Route>
        </Routes>
    );
}
